import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  ApplyAuditFixRequest,
  AuditFinding,
  AuditFixActionCode,
  AuditFixActions,
  AuditSummary,
  AuditTypeBreakdown,
  FindingTypeLabels,
  FIX_ACTIONS_BY_FINDING_TYPE,
  PaymentPlanAuditService,
  ScanRunMetadata
} from '../../services/payment-plan-audit.service';
import { ToastService } from '../../services/toast.service';
import { LoadingService } from '../../services/loading.service';
import { EnrollmentService } from '../../services/enrollment.service';
import { CorporateEnrollmentService } from '../../services/corporate-enrollment.service';
import { AuthService } from '../../services/auth.service';
import {
  AuditHubService,
  FindingProgressEvent,
  ScanFinishedEvent
} from '../../services/audit-hub.service';
import { ScanRunDetailDTO, ScanProgressItemDTO } from '../../interfaces/audit.interfaces';
import { BulkFixPreview, BulkFixRequest } from '../../interfaces/bulk-fix.interfaces';
import { Subscription, firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-payment-plan-audit',
  templateUrl: './payment-plan-audit.component.html',
  styleUrls: ['./payment-plan-audit.component.scss']
})
export class PaymentPlanAuditComponent implements OnInit, OnDestroy {
  summary: AuditSummary | null = null;
  findings: AuditFinding[] = [];
  filteredFindings: AuditFinding[] = [];

  // filtros
  selectedFindingType: string | null = null;
  selectedSeverity: 'high' | 'medium' | 'low' | null = null;
  selectedEnrollmentType: string | null = null;
  /**
   * Filtro por ID de inscripción. Se tipa como `string | number | null` porque
   * el input es `type="number"` y Angular bindea el valor como `number` cuando
   * el usuario tipea, pero como `''` cuando borra. Normalizamos a string en el
   * filtro para evitar `TypeError: ... .trim is not a function` (lo cual
   * rompía silenciosamente el filtro vía el handler `(ngModelChange)`).
   */
  searchEnrollmentId: string | number | null = '';

  isLoading = false;
  readonly typeLabels = FindingTypeLabels;

  // 🆕 Estado del scan async en vivo (Task 17).
  currentScan: ScanRunDetailDTO | null = null;
  checkingCurrent = false;
  /** True desde el click "Ejecutar" hasta que llega la respuesta del POST.
   * Sin esto, el botón seguía habilitado durante el RTT del POST → click
   * múltiple posible + sensación de "no hace nada". */
  startingScan = false;
  private hubSubs: Subscription[] = [];
  private pollingHandle?: ReturnType<typeof setInterval>;
  private hubDisconnectedAt?: number;

  /** True cuando hay un scan corriendo (en `currentScan`) o en proceso de arrancar. */
  get isRunning(): boolean {
    return this.startingScan || this.currentScan?.status === 'Running';
  }
  /** Habilita el botón "Ejecutar análisis". */
  get canStart(): boolean {
    return !this.isRunning && !this.checkingCurrent;
  }

  /**
   * 🆕 Histórico de scans + selección actual.
   * - `scanRuns`: lista poblada al cargar el componente.
   * - `selectedScanRunId`: null = el más reciente (default); cualquier otro = uno histórico.
   * - `scanPickerOpen`: estado del dropdown.
   * - `isExporting`: bloquea el botón de descarga mientras genera el Excel.
   */
  scanRuns: ScanRunMetadata[] = [];
  selectedScanRunId: string | null = null;
  scanPickerOpen = false;
  isExporting = false;

  /** 🆕 Estado del offcanvas de detalle. */
  detailOffcanvas: {
    open: boolean;
    finding: AuditFinding | null;
    installmentIds: number[];
    receiptIds: number[];
    versionIds: number[];
  } = { open: false, finding: null, installmentIds: [], receiptIds: [], versionIds: [] };

  /**
   * 🆕 Id del finding sobre el que el usuario está trabajando (offcanvas abierto
   * o fix modal abierto). La fila correspondiente se resalta visualmente en la
   * tabla. Persiste hasta que la corrección se aplica con éxito o el usuario
   * lo limpia explícitamente.
   */
  activeFindingId: number | null = null;

  // 🆕 Bulk fix masivo
  bulkPreview: BulkFixPreview | null = null;
  bulkLoading = false;
  bulkComment = '';
  showBulkModal = false;

  /** 🔐 Solo este email puede acceder al módulo de auditoría. */
  private readonly AUDIT_ALLOWED_EMAIL = 'jdanigo@cloudsoft.site';

  constructor(
    private auditService: PaymentPlanAuditService,
    private toast: ToastService,
    private loading: LoadingService,
    private router: Router,
    private enrollmentService: EnrollmentService,
    private corporateEnrollmentService: CorporateEnrollmentService,
    private authService: AuthService,
    private hubService: AuditHubService
  ) {}

  ngOnDestroy(): void {
    this.hubSubs.forEach(s => s.unsubscribe());
    if (this.pollingHandle) clearInterval(this.pollingHandle);
  }

  ngOnInit(): void {
    const email = (this.authService.getCurrentUser()?.email ?? '').toLowerCase();
    if (email !== this.AUDIT_ALLOWED_EMAIL.toLowerCase()) {
      this.router.navigate(['/dashboard']);
      return;
    }
    this.refresh();
    this.loadScanRuns();
    // 🆕 Task 17 — engancha SignalR + recupera scan en curso (si lo hay).
    // No bloquea la carga inicial: corre en background.
    void this.initAuditHub();
  }

  /**
   * 🆕 Task 17 — inicializa el flujo async:
   * 1. Pregunta al backend si hay un scan corriendo (`getCurrentScan`).
   * 2. Conecta el SignalR hub.
   * 3. Si había un scan corriendo, se suscribe a su grupo para recibir
   *    progreso en vivo.
   */
  async initAuditHub(): Promise<void> {
    this.checkingCurrent = true;
    try {
      this.currentScan = await firstValueFrom(this.auditService.getCurrentScan());
    } catch {
      // Silencioso — si falla, seguimos con el flujo legacy (history/summary).
    } finally {
      this.checkingCurrent = false;
    }

    try {
      await this.hubService.connect();
      this.wireHubSubscriptions();

      if (this.currentScan?.status === 'Running') {
        await this.hubService.subscribeToScan(this.currentScan.scanRunId);
        // Polling fallback también acá: si entramos a la página con un scan
        // ya corriendo (lanzado por otro tab/usuario o sobrevivió a un refresh),
        // SignalR puede no estar conectado todavía o haber perdido eventos.
        this.startPolling();
      }
    } catch {
      // El hub no es crítico para el render de history/summary.
    }
  }

  private wireHubSubscriptions(): void {
    if (this.hubSubs.length > 0) return; // ya cableado
    this.hubSubs.push(
      this.hubService.findingProgress$.subscribe(e => this.applyProgressEvent(e)),
      this.hubService.scanFinished$.subscribe(e => { void this.onScanFinished(e); }),
      this.hubService.scanCancelled$.subscribe(() => this.onScanCancelled()),
      this.hubService.connectionState$.subscribe(state => this.onHubState(state))
    );
  }

  private applyProgressEvent(e: FindingProgressEvent): void {
    if (!this.currentScan || this.currentScan.scanRunId !== e.scanRunId) return;
    const item = this.currentScan.items.find(i => i.findingType === e.findingType);
    if (!item) return;
    item.status = e.status as ScanProgressItemDTO['status'];
    if (e.findingsCount != null) item.findingsCount = e.findingsCount;
    if (e.durationMs != null) item.durationMs = e.durationMs;
    if (e.error) item.error = e.error;
  }

  /**
   * 🆕 Task 17 — handler en vivo del botón "Ejecutar análisis":
   * 1. Encola el scan async.
   * 2. Obtiene el snapshot inicial (14 items en Pending).
   * 3. Se suscribe al hub para recibir progreso.
   */
  async startScanLive(): Promise<void> {
    // Bloqueo INMEDIATO del botón — antes de cualquier I/O. canStart pasa a
    // false vía isRunning getter, el label muestra "Iniciando…".
    this.startingScan = true;
    try {
      const resp = await firstValueFrom(this.auditService.runScanAsync());
      this.currentScan = await firstValueFrom(this.auditService.getScanProgress(resp.scanRunId));
      // SignalR es optimization — el subscribe ocurre DESPUÉS del POST, por lo que
      // jobs muy rápidos pueden emitir eventos antes de que el cliente se suscriba
      // al group y los pierde. Arrancamos polling inmediato como fuente de verdad;
      // si SignalR llega, los eventos sobreescriben en applyProgressEvent (no race).
      await this.hubService.subscribeToScan(resp.scanRunId);
      this.startPolling();
      this.toast.showToast('info', 'Análisis iniciado. Verás el progreso en vivo.');
    } catch (err: any) {
      const baseMsg = err?.error?.error ?? err?.error?.message ?? err?.error ?? 'Error encolando el análisis.';
      this.toast.showToast('error', typeof baseMsg === 'string' ? baseMsg : 'Error.');
    } finally {
      // Liberamos el flag local; isRunning ahora depende de currentScan.status real.
      this.startingScan = false;
    }
  }

  /** 🆕 Cancela el scan actualmente en curso. */
  async cancelScan(): Promise<void> {
    if (!this.currentScan) return;
    const scanRunId = this.currentScan.scanRunId;
    try {
      await firstValueFrom(this.auditService.cancelScan(scanRunId));
      // Cleanup directo via onScanFinished — null out + apaga polling + toast + refresh.
      void this.onScanFinished({
        scanRunId,
        status: 'Cancelled',
        totalFindings: 0,
        finishedAt: new Date().toISOString()
      });
      return;
    } catch (err: any) {
      // Caso típico: el scan ya terminó en backend (Completed/Failed) y la UI
      // estaba desincronizada — refrescamos progreso para reflejar estado real
      // y disparar onScanFinished que limpia el panel.
      try {
        const fresh = await firstValueFrom(this.auditService.getScanProgress(scanRunId));
        this.currentScan = fresh;
        if (fresh.status !== 'Running') {
          void this.onScanFinished({
            scanRunId: fresh.scanRunId,
            status: fresh.status,
            totalFindings: fresh.totalFindings,
            finishedAt: fresh.finishedAt ?? new Date().toISOString()
          });
          this.toast.showToast('info', `El scan ya había terminado (${fresh.status}).`);
          return;
        }
      } catch { /* ignore — fallback al toast original */ }
      const baseMsg = err?.error?.error ?? err?.error?.message ?? 'No se pudo cancelar el scan.';
      this.toast.showToast('error', typeof baseMsg === 'string' ? baseMsg : 'Error.');
    }
  }

  /**
   * Cuando el scan termina, refrescamos progreso (estado final de las 14
   * cards), summary/findings/history para actualizar las vistas existentes.
   */
  private async onScanFinished(e: ScanFinishedEvent): Promise<void> {
    // Mantiene el panel visible con estados terminales (Completed/Failed/Skipped
    // por card). isRunning getter pasa a false porque currentScan.status != Running,
    // entonces el botón "Ejecutar análisis" se rehabilita pero el panel sigue.
    // El usuario lo cierra con el botón "X" cuando quiera.
    if (this.pollingHandle) { clearInterval(this.pollingHandle); this.pollingHandle = undefined; }
    try {
      this.currentScan = await firstValueFrom(this.auditService.getScanProgress(e.scanRunId));
    } catch {
      // Si falla el fetch, al menos actualizamos el header local con el evento.
      if (this.currentScan) {
        this.currentScan = {
          ...this.currentScan,
          status: e.status as ScanRunDetailDTO['status'],
          totalFindings: e.totalFindings,
          finishedAt: e.finishedAt
        };
      }
    }
    const verb = e.status === 'Cancelled' ? 'cancelado'
      : e.status === 'Failed' ? 'terminado con errores'
      : 'completado';
    this.toast.showToast('success', `Análisis ${verb}.`);
    this.selectedScanRunId = null;
    this.refresh();
    this.loadScanRuns();
  }

  private onScanCancelled(): void {
    if (this.pollingHandle) { clearInterval(this.pollingHandle); this.pollingHandle = undefined; }
    if (this.currentScan) {
      this.currentScan = { ...this.currentScan, status: 'Cancelled' };
    }
    this.toast.showToast('info', 'Scan cancelado.');
    this.refresh();
    this.loadScanRuns();
  }

  /** Cierra el panel de progreso (botón X). El scan ya terminó — los hallazgos
   * permanecen visibles en el bloque "Hallazgos por tipo". */
  closeScanPanel(): void {
    this.currentScan = null;
  }

  /**
   * Si el hub se desconecta más de 10s y todavía hay un scan corriendo,
   * arrancamos polling de respaldo cada 4s sobre `getScanProgress`.
   * Al reconectar paramos el polling.
   */
  private onHubState(state: 'connected' | 'disconnected'): void {
    if (state === 'disconnected') {
      this.hubDisconnectedAt = Date.now();
      setTimeout(() => {
        if (this.hubDisconnectedAt
            && Date.now() - this.hubDisconnectedAt > 10000
            && this.isRunning) {
          this.startPolling();
        }
      }, 10000);
    } else {
      this.hubDisconnectedAt = undefined;
      if (this.pollingHandle) {
        clearInterval(this.pollingHandle);
        this.pollingHandle = undefined;
      }
    }
  }

  private startPolling(): void {
    if (this.pollingHandle || !this.currentScan) return;
    this.pollingHandle = setInterval(async () => {
      if (!this.currentScan) return;
      try {
        this.currentScan = await firstValueFrom(
          this.auditService.getScanProgress(this.currentScan.scanRunId)
        );
      } catch { /* swallow polling errors */ }
      if (!this.isRunning && this.pollingHandle) {
        clearInterval(this.pollingHandle);
        this.pollingHandle = undefined;
        // Estado terminal alcanzado vía polling — disparar el refresh de
        // summary + findings (equivalente a onScanFinished, sin event).
        if (this.currentScan && this.currentScan.scanRunId) {
          void this.onScanFinished({
            scanRunId: this.currentScan.scanRunId,
            status: this.currentScan.status,
            totalFindings: this.currentScan.totalFindings,
            finishedAt: this.currentScan.finishedAt ?? new Date().toISOString()
          });
        }
      }
    }, 2000);
  }

  /** Re-carga summary + findings del scan actualmente seleccionado (o el más reciente). */
  refresh(): void {
    this.isLoading = true;
    const sid = this.selectedScanRunId;
    this.auditService.getSummary(sid).subscribe({
      next: (res: any) => {
        this.summary = res?.data ?? res;
      },
      error: () => this.toast.showToast('error', 'No se pudo cargar el resumen.'),
      complete: () => { /* dejamos loading hasta findings */ }
    });

    // 🔧 take=500 — más allá de 500 hallazgos exportar a Excel.
    this.auditService.getFindings({ take: 500, scanRunId: sid }).subscribe({
      next: (res: any) => {
        this.findings = res?.data ?? res ?? [];
        this.applyFilters();
        this.isLoading = false;
      },
      error: () => {
        this.toast.showToast('error', 'No se pudieron cargar los hallazgos.');
        this.isLoading = false;
      }
    });
  }

  /** 🆕 Carga el histórico de scans para alimentar el selector. */
  loadScanRuns(): void {
    this.auditService.getScanRuns(50).subscribe({
      next: (res: any) => {
        this.scanRuns = res?.data ?? res ?? [];
      },
      error: () => this.toast.showToast('error', 'No se pudo cargar el histórico de análisis.')
    });
  }

  /**
   * 🆕 Selecciona un scan del histórico. `null` = el más reciente (default).
   * Recarga summary + findings para ese scan.
   */
  selectScanRun(scanRunId: string | null): void {
    this.selectedScanRunId = scanRunId;
    this.scanPickerOpen = false;
    this.refresh();
  }

  /** 🆕 Toggle del dropdown del selector de scans. */
  toggleScanPicker(): void {
    this.scanPickerOpen = !this.scanPickerOpen;
  }

  /** 🆕 Metadata del scan actualmente seleccionado (para mostrar en el header). */
  get currentScanMeta(): ScanRunMetadata | null {
    if (!this.scanRuns.length) return null;
    if (this.selectedScanRunId == null) {
      return this.scanRuns.find(s => s.isLatest) ?? this.scanRuns[0] ?? null;
    }
    return this.scanRuns.find(s => s.scanRunId === this.selectedScanRunId) ?? null;
  }

  /**
   * 🆕 Exporta el scan actual a Excel.
   * Usa `responseType: 'blob'` + extrae filename del header `Content-Disposition`.
   */
  exportExcel(): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.loading.show();
    this.auditService.exportExcel(this.selectedScanRunId)
      .pipe(finalize(() => {
        this.isExporting = false;
        this.loading.hide();
      }))
      .subscribe({
        next: (response) => {
          const blob = response.body;
          if (!blob) {
            this.toast.showToast('error', 'Respuesta vacía del servidor.');
            return;
          }
          // Filename del header `Content-Disposition`, fallback a uno default.
          let filename = 'Auditoria_Planes.xlsx';
          const disp = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
          if (disp) {
            const match = /filename\*?=(?:UTF-8'')?\"?([^\";]+)\"?/i.exec(disp);
            if (match && match[1]) filename = decodeURIComponent(match[1]);
          }
          // Trigger download.
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          this.toast.showToast('success', 'Reporte Excel generado.');
        },
        error: () => this.toast.showToast('error', 'Error generando el reporte Excel.')
      });
  }

  /**
   * Handler del botón "Ejecutar análisis".
   * 🆕 Task 17 — ahora arranca el flujo en vivo con SignalR + polling de
   * respaldo. La página no se bloquea y se ve el progreso por finding
   * en las 14 cards.
   */
  runScan(): void {
    if (!this.canStart) return;
    void this.startScanLive();
  }

  applyFilters(): void {
    // Invalidamos el preview cuando cambian filtros para forzar recálculo.
    this.bulkPreview = null;
    this.filteredFindings = this.findings.filter(f => {
      if (this.selectedFindingType && f.findingType !== this.selectedFindingType) return false;
      if (this.selectedSeverity && f.severity !== this.selectedSeverity) return false;
      if (this.selectedEnrollmentType && f.enrollmentType !== this.selectedEnrollmentType) return false;
      // 🆕 Normalizamos a string antes de trim porque el input type="number"
      // puede llegar como number (cuando hay valor) o como '' (cuando se borra).
      const rawId = this.searchEnrollmentId == null ? '' : String(this.searchEnrollmentId).trim();
      if (rawId) {
        const id = Number(rawId);
        if (!isNaN(id)) {
          // Comparamos contra el ID mostrado en la tabla. Para corporativas el
          // backend reporta tanto enrollmentId como corporateEnrollmentId — un
          // match en cualquiera de los dos cuenta como hit.
          const matchesEnrollment = f.enrollmentId === id;
          const matchesCorporate = f.corporateEnrollmentId === id;
          if (!matchesEnrollment && !matchesCorporate) return false;
        }
      }
      return true;
    });
  }

  filterByType(type: string): void {
    this.selectedFindingType = this.selectedFindingType === type ? null : type;
    this.applyFilters();
  }

  filterBySeverity(sev: 'high' | 'medium' | 'low'): void {
    this.selectedSeverity = this.selectedSeverity === sev ? null : sev;
    this.applyFilters();
  }

  clearFilters(): void {
    this.selectedFindingType = null;
    this.selectedSeverity = null;
    this.selectedEnrollmentType = null;
    this.searchEnrollmentId = null;
    this.applyFilters();
  }

  // ===== Bulk fix masivo =====

  /** Habilita botones bulk si findingType actual es soportado. */
  get canBulkFix(): boolean {
    if ((this.filteredFindings?.length ?? 0) === 0) return false;
    if (this.selectedFindingType === 'CUOTAS_SIN_VERSION'
        || this.selectedFindingType === 'VERSION_HUERFANA') return true;
    // CUOTAS_MULTIVERSION es individual-only (no hay consolidate corporativo).
    if (this.selectedFindingType === 'CUOTAS_MULTIVERSION') return this.bulkScope === 'individual';
    if (this.selectedFindingType === 'LINEAGE_DUPLICADO') return this.bulkScope === 'individual';
    return false;
  }

  /** Mapea el filtro UI al scope que entiende el backend. */
  get bulkScope(): 'individual' | 'corporate' | null {
    if (!this.selectedEnrollmentType) return null;
    return this.selectedEnrollmentType === 'individual' ? 'individual' : 'corporate';
  }

  /** Construye request reusable para preview / start. */
  private buildBulkRequest(commentOverride?: string): BulkFixRequest | null {
    if (!this.bulkScope || !this.selectedFindingType) return null;
    const idRaw = this.searchEnrollmentId == null ? '' : String(this.searchEnrollmentId).trim();
    const filterId = idRaw && !isNaN(Number(idRaw)) ? Number(idRaw) : null;
    return {
      scanRunId: this.selectedScanRunId ?? this.summary?.lastScanRunId ?? null,
      findingType: this.selectedFindingType as 'CUOTAS_SIN_VERSION' | 'VERSION_HUERFANA' | 'CUOTAS_MULTIVERSION' | 'LINEAGE_DUPLICADO',
      scope: this.bulkScope,
      comment: commentOverride ?? 'preview-only',
      filterEnrollmentId: this.bulkScope === 'individual' ? filterId : null,
      filterCorporateEnrollmentId: this.bulkScope === 'corporate' ? filterId : null
    };
  }

  async loadBulkPreview(): Promise<void> {
    const req = this.buildBulkRequest();
    if (!req) {
      this.toast.showToast('error', 'Filtrá por tipo de inscripción primero.');
      return;
    }
    this.bulkLoading = true;
    try {
      this.bulkPreview = await firstValueFrom(this.auditService.previewBulkFix(req));
    } catch {
      this.toast.showToast('error', 'No se pudo cargar la vista previa.');
    } finally {
      this.bulkLoading = false;
    }
  }

  openBulkModal(): void {
    if (!this.bulkPreview || this.bulkPreview.totalOperations === 0) {
      this.toast.showToast('error', 'Cargá la vista previa primero.');
      return;
    }
    this.bulkComment = '';
    this.showBulkModal = true;
  }

  closeBulkModal(): void {
    this.showBulkModal = false;
    this.bulkComment = '';
  }

  async confirmBulkFix(): Promise<void> {
    if (!this.bulkComment || this.bulkComment.trim().length < 10) {
      this.toast.showToast('error', 'Motivo obligatorio (mín. 10 caracteres).');
      return;
    }
    const req = this.buildBulkRequest(this.bulkComment.trim());
    if (!req) {
      this.toast.showToast('error', 'Filtros inválidos.');
      return;
    }
    try {
      const resp = await firstValueFrom(this.auditService.startBulkFix(req));
      this.closeBulkModal();
      this.router.navigate(['/admin/payment-plan-audit/bulk-fix', resp.batchId]);
    } catch (err: any) {
      const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo iniciar el bulk fix.';
      this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error.');
    }
  }

  /**
   * 🆕 Abre el detalle de la inscripción afectada en una NUEVA pestaña.
   * Individuales → view-v2 (vista moderna, no la legacy).
   * Corporativas → vista del acuerdo corporativo.
   */
  goToEnrollment(f: AuditFinding): void {
    const segments = f.enrollmentType === 'individual'
      ? ['/dashboard/enrollments/view-v2', f.enrollmentId]
      : ['/dashboard/enrollments/corporate/view-v2', f.corporateEnrollmentId ?? f.enrollmentId];
    const url = this.router.serializeUrl(this.router.createUrlTree(segments));
    window.open(url, '_blank');
  }

  /**
   * 🆕 Abre los Comprobantes de Ingreso en una nueva pestaña, dirigiendo al
   * módulo correcto (individuales vs empresariales) y pre-filtrando por el
   * criterio más útil:
   *  - Individuales → módulo individual filtrado por documento del participante
   *  - Corporativas → módulo empresarial filtrado por nombre/NIT de la empresa
   */
  goToReceipts(f: AuditFinding): void {
    if (f.enrollmentType === 'individual') {
      this.loading.show();
      this.enrollmentService.getById(f.enrollmentId).subscribe({
        next: (res: any) => {
          this.loading.hide();
          const enrollment = res?.data ?? res;
          const documentId = enrollment?.participant?.documentId
            ?? enrollment?.participant?.documentNumber
            ?? '';
          // 🆕 También enviamos el projectId numérico para que la vista de
          // comprobantes filtre por documento + proyecto, evitando que se
          // mezclen pagos del mismo participante en otros proyectos.
          const projectId = enrollment?.project?.id ?? null;
          this.openIndividualReceiptsTab(documentId || String(f.enrollmentId), projectId);
        },
        error: () => {
          this.loading.hide();
          this.openIndividualReceiptsTab(String(f.enrollmentId), null);
        }
      });
    } else {
      // Corporativas: lookup del acuerdo para obtener nombre/NIT de la empresa
      const ceid = f.corporateEnrollmentId ?? f.enrollmentId;
      this.loading.show();
      this.enrollmentService.getCorporateDetails(ceid).subscribe({
        next: (res: any) => {
          this.loading.hide();
          const details = res?.data ?? res;
          // Mejor criterio de búsqueda: NIT > razón social > id del acuerdo
          const search = details?.company?.nit
                      ?? details?.company?.identification
                      ?? details?.company?.businessName
                      ?? details?.company?.name
                      ?? String(ceid);
          // 🆕 projectId del acuerdo corporativo para co-filtrar comprobantes.
          const projectId = details?.project?.id ?? null;
          this.openBusinessReceiptsTab(String(search), projectId);
        },
        error: () => {
          this.loading.hide();
          this.openBusinessReceiptsTab(String(ceid), null);
        }
      });
    }
  }

  /** Abre /income-receipts/individual con search (y opcionalmente projectId) precargado. */
  private openIndividualReceiptsTab(searchTerm: string, projectId: number | null = null): void {
    const queryParams: any = { search: searchTerm };
    if (projectId) queryParams.projectId = projectId;
    const tree = this.router.createUrlTree(
      ['/dashboard/income-receipts/individual'],
      { queryParams }
    );
    window.open(this.router.serializeUrl(tree), '_blank');
  }

  /** Abre /income-receipts/business con search (y opcionalmente projectId) precargado. */
  private openBusinessReceiptsTab(searchTerm: string, projectId: number | null = null): void {
    const queryParams: any = { search: searchTerm };
    if (projectId) queryParams.projectId = projectId;
    const tree = this.router.createUrlTree(
      ['/dashboard/income-receipts/business'],
      { queryParams }
    );
    window.open(this.router.serializeUrl(tree), '_blank');
  }

  /**
   * Alias retrocompatible: si algún código viejo llamaba `openReceiptsTab`,
   * usar el módulo individual por defecto.
   */
  private openReceiptsTab(searchTerm: string): void {
    this.openIndividualReceiptsTab(searchTerm);
  }

  // ============================================================
  // 🆕 OFFCANVAS DE DETALLE DEL FINDING
  // ============================================================

  /** Parsea un CSV de IDs (ej "16019,16020,16021") a un array numérico. */
  private parseCsvIds(csv: string | null | undefined): number[] {
    if (!csv) return [];
    return csv.split(',')
      .map(s => Number(s.trim()))
      .filter(n => !isNaN(n) && n > 0);
  }

  /** Abre el offcanvas con el detalle completo del finding. */
  openDetail(f: AuditFinding): void {
    this.detailOffcanvas = {
      open: true,
      finding: f,
      installmentIds: this.parseCsvIds(f.affectedInstallmentIds),
      receiptIds: this.parseCsvIds(f.affectedReceiptIds),
      versionIds: this.parseCsvIds(f.affectedVersionIds)
    };
    this.activeFindingId = f.id;
  }

  closeDetail(): void {
    this.detailOffcanvas.open = false;
    // NO limpiamos activeFindingId: la marca visual de la fila persiste para
    // que el usuario no pierda de vista cuál finding estaba revisando.
  }

  /** Limpieza manual de la marca visual (botón "X" o "Limpiar marca"). */
  clearActiveFinding(): void {
    this.activeFindingId = null;
  }

  /**
   * 🆕 Atajo: desde el offcanvas, pasar directo al modal de corrección.
   * Cierra el offcanvas pero mantiene la marca visual en la fila.
   */
  openFixFromDetail(): void {
    const f = this.detailOffcanvas.finding;
    if (!f) return;
    this.closeDetail();
    this.openFixModal(f);
  }

  /**
   * Abre el historial de planes de pagos en nueva pestaña.
   * El historial muestra todas las cuotas con su ID — útil para localizar
   * una cuota específica afectada por el finding.
   */
  openHistoryTab(): void {
    const f = this.detailOffcanvas.finding;
    if (!f) return;
    const enrollmentId = f.enrollmentType === 'individual'
      ? f.enrollmentId
      : (f.corporateEnrollmentId ?? f.enrollmentId);
    const queryParams: any = {
      type: f.enrollmentType === 'individual' ? 'individual' : 'corporate'
    };
    if (f.participantId) queryParams.participantId = f.participantId;
    const tree = this.router.createUrlTree(
      ['/dashboard/enrollments/payment-plans/history', enrollmentId],
      { queryParams }
    );
    window.open(this.router.serializeUrl(tree), '_blank');
  }

  /**
   * Abre un comprobante específico (por id) en nueva pestaña, dirigiendo al
   * módulo correcto según el finding del offcanvas (individual vs business).
   */
  openSingleReceiptTab(receiptId: number): void {
    const finding = this.detailOffcanvas.finding;
    const isCorporate = finding && finding.enrollmentType !== 'individual';
    if (isCorporate) {
      this.openBusinessReceiptsTab(String(receiptId));
    } else {
      this.openIndividualReceiptsTab(String(receiptId));
    }
  }

  /** Severidad → label legible. */
  severityLabel(sev: string): string {
    switch (sev) {
      case 'high': return 'Crítica';
      case 'medium': return 'Media';
      case 'low': return 'Baja';
      default: return sev;
    }
  }

  typeLabel(t: string): string { return this.typeLabels[t] ?? t; }

  severityColor(sev: string): string {
    switch (sev) {
      case 'high': return 'bg-rose-100 text-rose-800 border-rose-300';
      case 'medium': return 'bg-amber-100 text-amber-800 border-amber-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  }

  severityDot(sev: string): string {
    switch (sev) {
      case 'high': return 'bg-rose-500';
      case 'medium': return 'bg-amber-500';
      default: return 'bg-gray-400';
    }
  }

  enrollmentTypeLabel(t: string): string {
    switch (t) {
      case 'individual': return 'Individual';
      case 'corporate-company': return 'Corp · Empresa';
      case 'corporate-participant': return 'Corp · Participante';
      default: return t;
    }
  }

  trackById = (_: number, f: AuditFinding) => f.id;
  trackByType = (_: number, b: AuditTypeBreakdown) => b.findingType + b.severity;

  // ====================================================================
  // 🆕 Modal de corrección caso por caso
  // ====================================================================
  showFixModal = false;
  fixingFinding: AuditFinding | null = null;
  availableActions: Array<{ code: AuditFixActionCode; label: string; description: string }> = [];
  selectedActionCode: AuditFixActionCode | null = null;
  fixComment = '';
  fixNewAmountToPay: number | null = null;
  fixNewTotalAmount: number | null = null;
  fixNewInstallmentAmount: number | null = null;
  isApplyingFix = false;

  /** Helpers — qué acción está seleccionada. */
  get isAction() {
    return {
      rescueOrphan: this.selectedActionCode === AuditFixActions.RescueOrphanReceipt,
      reactivateArchived: this.selectedActionCode === AuditFixActions.ReactivateArchivedInstallment,
      convertToPartial: this.selectedActionCode === AuditFixActions.ConvertPaidToPartial,
      adjustPaidAmount: this.selectedActionCode === AuditFixActions.AdjustPaidAmount,
      markAsPaid: this.selectedActionCode === AuditFixActions.MarkAsPaid,
      adjustTotal: this.selectedActionCode === AuditFixActions.AdjustEnrollmentTotal,
      assignToVersion: this.selectedActionCode === AuditFixActions.AssignToVersion,
      deleteOrphanVersion: this.selectedActionCode === AuditFixActions.DeleteOrphanVersion,
    };
  }

  openFixModal(f: AuditFinding): void {
    this.fixingFinding = f;
    this.activeFindingId = f.id;
    this.availableActions = FIX_ACTIONS_BY_FINDING_TYPE[f.findingType] ?? [];
    this.selectedActionCode = this.availableActions[0]?.code ?? null;
    this.fixComment = '';
    this.fixNewAmountToPay = null;
    this.fixNewInstallmentAmount = null;
    // Para ADJUST_ENROLLMENT_TOTAL precargamos con la suma de cuotas (lo "deseado")
    this.fixNewTotalAmount = f.amountDelta != null && f.findingType === 'TOTAL_NO_CUADRA'
      ? null // el operador lo decide; mostraremos sugerencia en el modal
      : null;
    this.showFixModal = true;
  }

  closeFixModal(): void {
    this.showFixModal = false;
    this.fixingFinding = null;
  }

  /** Primer Id afectado (de la CSV string que viene del backend). */
  firstAffectedId(csv?: string | null): number | null {
    if (!csv) return null;
    const first = csv.split(',')[0]?.trim();
    const n = Number(first);
    return isNaN(n) ? null : n;
  }

  /** Lee el TotalAmount sugerido del detalle del hallazgo (parsing barato del Detail). */
  suggestedTotalFromDetail(): number | null {
    if (!this.fixingFinding?.detail) return null;
    // Detail tipo: "TotalAmount=1.500.000 vs Suma cuotas activas=1.875.000 (diferencia=-375.000)"
    const m = /Suma cuotas activas[=:]\s*([\d.,]+)/i.exec(this.fixingFinding.detail);
    if (!m) return null;
    const num = Number(m[1].replace(/\./g, '').replace(',', '.'));
    return isNaN(num) ? null : num;
  }

  confirmFix(): void {
    if (!this.fixingFinding || !this.selectedActionCode) return;
    if (!this.fixComment || this.fixComment.trim().length < 10) {
      this.toast.showToast('error', 'Comentario obligatorio (mínimo 10 caracteres).');
      return;
    }

    const f = this.fixingFinding;
    const req: ApplyAuditFixRequest = {
      findingId: f.id,
      action: this.selectedActionCode,
      versionReason: 5, // Adjustment
      comment: this.fixComment.trim()
    };

    // Completar params según la acción
    switch (this.selectedActionCode) {
      case AuditFixActions.RescueOrphanReceipt:
        req.receiptId = this.firstAffectedId(f.affectedReceiptIds) ?? undefined;
        if (this.fixNewInstallmentAmount != null) req.newInstallmentAmount = this.fixNewInstallmentAmount;
        if (!req.receiptId) {
          this.toast.showToast('error', 'No se identificó el recibo a rescatar.');
          return;
        }
        break;
      case AuditFixActions.ConvertPaidToPartial:
      case AuditFixActions.MarkAsPaid:
      case AuditFixActions.ReactivateArchivedInstallment:
        req.installmentId = this.firstAffectedId(f.affectedInstallmentIds) ?? undefined;
        if (!req.installmentId) {
          this.toast.showToast('error', 'No se identificó la cuota afectada.');
          return;
        }
        break;
      case AuditFixActions.AdjustPaidAmount:
        req.installmentId = this.firstAffectedId(f.affectedInstallmentIds) ?? undefined;
        if (!req.installmentId || this.fixNewAmountToPay == null || this.fixNewAmountToPay <= 0) {
          this.toast.showToast('error', 'Cuota y nuevo AmountToPay (>0) requeridos.');
          return;
        }
        req.newAmountToPay = this.fixNewAmountToPay;
        break;
      case AuditFixActions.AdjustEnrollmentTotal:
        req.enrollmentId = f.enrollmentId;
        req.enrollmentType = f.enrollmentType;
        if (this.fixNewTotalAmount == null || this.fixNewTotalAmount <= 0) {
          this.toast.showToast('error', 'Nuevo TotalAmount (>0) requerido.');
          return;
        }
        req.newTotalAmount = this.fixNewTotalAmount;
        break;
      case AuditFixActions.AssignToVersion:
        req.enrollmentId = f.enrollmentId;
        req.enrollmentType = f.enrollmentType;
        break;
      case AuditFixActions.SplitSharedLineage:
        req.enrollmentId = f.enrollmentId;
        req.enrollmentType = f.enrollmentType;
        break;
      case AuditFixActions.DeleteOrphanVersion:
        req.versionId = this.firstAffectedId(f.affectedVersionIds) ?? undefined;
        if (!req.versionId) {
          this.toast.showToast('error', 'No se identificó la versión a eliminar.');
          return;
        }
        break;
    }

    this.isApplyingFix = true;
    this.loading.show();
    this.auditService.applyFix(req).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        this.toast.showToast('success', data?.message ?? 'Corrección aplicada.');
        this.closeFixModal();
        // El finding fue resuelto: limpiar la marca visual
        this.activeFindingId = null;
        // Re-correr scan async (el sync endpoint /run fue eliminado).
        // No esperamos el resultado: el botón "Ejecutar análisis" muestra
        // progreso vía SignalR si el usuario quiere ver el re-scan completo.
        void this.startScanLive().catch(() => { /* swallow — refresh() abajo igual carga summary actual */ });
        this.refresh();
      },
      error: (err) => {
        const msg = err?.error?.message ?? err?.error ?? 'No se pudo aplicar la corrección.';
        this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error aplicando corrección.');
      },
      complete: () => {
        this.isApplyingFix = false;
        this.loading.hide();
      }
    });
  }
}
